import { AddressController } from '@adornis/address/client/address-controller.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import '@adornis/charts/chartjs/line-chart.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { SingleCumulativeLevumiStat } from '@chancenwerk/campus-learning-content/api/levumiAPI.js';
import { CampusLevumiLevel } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiLevel.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import { LevumiMathCategory, LevumiSubject } from '@chancenwerk/db/enums/CampusLevumiEnums.js';
import { Bundesland } from '@chancenwerk/db/enums/enums.js';
import { KpfFilter } from '@chancenwerk/db/filter/KpfFilter.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-campus-loader.js';
import '@chancenwerk/design/components/cr-card.js';
import '@chancenwerk/design/components/cr-date-picker-dropdown.js';
import '@chancenwerk/design/components/cr-dropdown-multiselection.js';
import '@chancenwerk/design/components/cr-dropdown-search-selection.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import { KPFAPI } from '@chancenwerk/master-data-management/kpf/api/KPFAPI.js';
import type { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import type { DateTime } from 'luxon';
import { combineLatest, firstValueFrom, of, switchMap } from 'rxjs';
import './campus-page-no-permission.js';

@customElement('campus-page-student-knowledge-cumulative-diagnostik')
export class CampusPageStudentKnowledgeCumulativeDiagnostik extends CrPage {
  private _addressController = new AddressController(this, { language: 'de' });

  @state() private readonly _startDate = new RXController(this, null as Maybe<DateTime>);
  @state() private readonly _endDate = new RXController(this, null as Maybe<DateTime>);
  @state() private readonly _kpfs = new RXController(this, [] as KPF[]);
  @state() private readonly _bundeslaender = new RXController(this, [] as Bundesland[]);
  @state() private readonly _cities = new RXController(this, [] as string[]);
  @state() private readonly _subject = new RXController(this, LevumiSubject.MATH);
  @state() private readonly _mathCategory = new RXController(this, LevumiMathCategory.ADD as LevumiMathCategory);
  @state() private readonly _cll = new RXController(this, null as Maybe<CampusLevumiLevel>);
  @state() private readonly _count = new RXController<Maybe<number>>(this, null);

  @state() private readonly _allLevels = new RXController(
    this,
    this._subject.observable.pipe(
      switchMap(subject => LearningContentAPI.levumi.subscribeLevelsBySubject(subject)(CampusLevumiLevel.allFields)),
    ),
  );

  @state() private readonly _stats = new RXController(
    this,
    combineLatest([
      this._startDate.observable,
      this._endDate.observable,
      this._bundeslaender.observable,
      this._cities.observable,
      this._kpfs.observable,
      this._mathCategory.observable,
      this._cll.observable,
      this._count.observable,
    ]).pipe(
      switchMap(t =>
        LearningContentAPI.levumi.getCumulativeLevumiStats(
          t[0] ?? null, // start date
          t[1] ?? null, // end date
          t[2] ?? null, // bundeslaender
          t[3] ?? null, // cities
          t[4]?.map(kpf => kpf._id) ?? null, // kpf ids
          t[5], // math category
          t[6]?.subject ?? null, // subject
          t[6]?.level ?? null, // level
          t[7] ?? null, // count
        )(SingleCumulativeLevumiStat.allFields),
      ),
      switchMap(stats => {
        if (!stats || !stats.length) return of(null);
        const data = {
          labels: stats.map((_, i) => `${i + 1} (N = ${stats[i]?.count ?? 0})`),
          datasets: [
            {
              label: 'durchschnittliche Entwicklung aller abgebildeten Teilnehmenden',
              data: stats.map(s => s.average),
              borderColor: '#E28807',
            },
            // {
            //   label: 'Anzahl an Tests',
            //   data: stats.map(s => s.count),
            // },
          ],
        };
        return of(data);
      }),
    ),
  );

  constructor() {
    super();
    this.section = false;
    this.label = 'Kumulative Auswertung';
    this.description =
      'Hier kannst du eine kumulative Auswertung der LEVUMI Testdaten betrachten. Dafür kannst du hier verschiedene Filter einstellen. Die relevanten Tests werden nach Schüler:in sortiert und dann nach ihrer Position zusammengefügt. Es werden alle ersten Tests der Schüler:innen zusammengeführt und daraus der Durchschnitt berechnet. Genauso mit allen zweiten, dritten, vierten ... Tests.';
    this.permission = ChancenPermission.campus.READ_DIAGNOSTIC_CUM;
  }

  protected override content(): Renderable {
    return html`<cr-flex>
      ${this._selectors()} ${this._graph()}
      <div ${css({ height: '60px' })}></div>
    </cr-flex>`;
  }

  private _graph(): Renderable {
    if (this._stats.isLoading) return this._loader();
    if (!this._stats.value || !this._stats.value.labels?.length)
      return html`
        <cr-card margin="sm" space="lg" crossaxis-center center>
          <cr-flex center crossaxis-center space="sm">
            <cr-text>${this._cll.value ? 'Hierzu gibt es keine Daten.' : 'Das Level muss gewählt werden.'}</cr-text>
          </cr-flex>
        </cr-card>
      `;

    return html`
      <cr-card space="lg" margin="sm">
        <cr-flex space="lg">
          <line-chart
            .data=${this._stats.value}
            .options=${{
              responsive: true,
              plugins: {
                legend: {
                  labels: {
                    font: {
                      size: 20,
                    },
                  },
                },
              },
              scales: {
                y: {
                  title: {
                    display: true,
                    text: 'durchschnittliche Anzahl richtiger Antworten',
                    font: {
                      size: 20,
                    },
                  },
                  beginAtZero: true,
                  ticks: {
                    font: {
                      size: 20,
                    },
                  },
                },
                x: {
                  title: {
                    display: true,
                    text: 'Testzeitpunkt (N = Anzahl der Teilnehmenden zu diesem Zeitpunkt)',
                    font: {
                      size: 20,
                    },
                  },
                  ticks: {
                    font: {
                      size: 20,
                    },
                  },
                },
              },
            }}
          >
          </line-chart>
        </cr-flex>
      </cr-card>
    `;
  }

  private _loader(): Renderable {
    return html`
      <cr-flex center crossaxis-center w100 h100>
        <cr-campus-loader> </cr-campus-loader>
      </cr-flex>
    `;
  }

  private _selectors(): Renderable {
    return html`
      <cr-card padding="sm" margin="sm">
        <cr-flex space="md" padding="sm">
          ${this._timeframeSelector()} ${this._kpfSelector()} ${this._bundeslandSelector()} ${this._citySelector()}
          ${this._subjectSelector()}
        </cr-flex>
      </cr-card>
    `;
  }

  private _timeframeSelector(): Renderable {
    return html`
      <cr-flex horizontal space="sm" crossaxis-center>
        <cr-date-picker-dropdown
          clearable
          click-to-save
          flex
          placeholder="Startdatum"
          @value-picked=${(e: ValueEvent<Maybe<DateTime>>) => {
            this._startDate.next(e.detail.value?.startOf('day') ?? null);
          }}
        >
        </cr-date-picker-dropdown>
        <cr-text> bis </cr-text>
        <cr-date-picker-dropdown
          clearable
          click-to-save
          flex
          placeholder="Enddatum"
          @value-picked=${(e: ValueEvent<Maybe<DateTime>>) => {
            this._endDate.next(e.detail.value?.endOf('day') ?? null);
          }}
        >
        </cr-date-picker-dropdown>
        <cr-text> mit </cr-text>
        <cr-input
          type="number"
          clearable
          flex
          placeholder="Anzahl der Testzeitpunkte"
          @value-picked=${(e: ValueEvent<Maybe<number>>) => {
            this._count.next(e.detail.value);
          }}
        >
        </cr-input>
      </cr-flex>
    `;
  }

  private _kpfSelector(): Renderable {
    return html`
      <cr-dropdown-multiselection
        clearable
        placeholder="Aus welchen KPF / Schulen sollen die Daten kommen?"
        placeholder-mode="hide"
        .renderString=${(kpf: KPF) => `${kpf.kuerzel} | ${kpf.school?.name}`}
        .search=${(search: Maybe<string>) =>
          firstValueFrom(
            KPFAPI.subscribeByFilter(
              0,
              20,
              new KpfFilter({ search }),
            )({ kuerzel: 1, schoolID: 1, school: { name: 1, kuerzel: 1 }, produkt: { kuerzel: 1 } }),
          )}
        @value-picked=${(e: ValueEvent<Maybe<KPF[]>>) => {
          this._kpfs.next(e.detail.value ?? []);
        }}
      >
      </cr-dropdown-multiselection>
    `;
  }

  private _bundeslandSelector(): Renderable {
    return html`
      <cr-dropdown-multiselection
        clearable
        placeholder="Welche Bundesländer sollen beachtet werden?"
        placeholder-mode="hide"
        .selectables=${Object.values(Bundesland)}
        @value-picked=${(e: ValueEvent<Maybe<Bundesland[]>>) => {
          this._bundeslaender.next(e.detail.value ?? []);
        }}
      ></cr-dropdown-multiselection>
    `;
  }

  private _citySelector(): Renderable {
    return html`
      <cr-dropdown-search-selection
        flex
        icon="location_city"
        placeholder="Städte hinzufügen"
        ${this._addressController.city()}
        @value-picked=${(e: ValueEvent<string>) => {
          const value = e.detail.value;
          if (!value.includes(',')) this._cities.next((this._cities.value ?? []).concat(value));
        }}
      >
      </cr-dropdown-search-selection>
      ${repeat(this._cities.value ?? [], (city, index) => {
        return html`
          <cr-card crossaxis-center flex space="sm" padding="sm" horizontal space-between>
            <cr-text>${city}</cr-text>
            <cr-button
              mode="icon"
              icon="delete"
              tone="error"
              @click=${() => {
                this._cities.next(this._cities.value!.splice(index, 1));
              }}
            >
            </cr-button>
          </cr-card>
        `;
      })}
    `;
  }

  private _subjectSelector(): Renderable {
    return html`
      <cr-flex horizontal w100 space-between>
        <cr-dropdown-selection
          ${css({ flexGrow: '1', marginRight: '10px' })}
          placeholder="Fach"
          .selectables=${Object.values(LevumiSubject)}
          .renderString=${(item: Maybe<LevumiSubject>): string => {
            switch (item) {
              case LevumiSubject.GERMAN:
                return 'Deutsch';
              case LevumiSubject.MATH:
                return 'Mathe';
              case LevumiSubject.NUMBER_DICTATION:
                return 'Zahlendiktat';
              default:
                return '';
            }
          }}
          .value=${this._subject}
          @value-picked=${(e: ValueEvent<LevumiSubject>) => {
            this._cll.next(null);
          }}
        >
        </cr-dropdown-selection>
        ${this._subject.value === LevumiSubject.MATH
          ? html`
              <cr-dropdown-selection
                ${css({ flexGrow: '1', margin: '0 10px' })}
                placeholder="Kategorie"
                .value=${this._mathCategory}
                .selectables=${Object.values(LevumiMathCategory)}
              >
              </cr-dropdown-selection>
            `
          : nothing}
        <cr-dropdown-selection
          ${css({ flexGrow: '1', marginLeft: '10px' })}
          placeholder="Level"
          .selectables=${this._getRelevantLevels()}
          .renderString=${(cll: Maybe<CampusLevumiLevel>) => cll?.level ?? ''}
          .value=${this._cll.value}
          @value-picked=${(e: ValueEvent<CampusLevumiLevel>) => {
            this._cll.next(e.detail.value);
          }}
        >
        </cr-dropdown-selection>
      </cr-flex>
    `;
  }

  private _getRelevantLevels(): CampusLevumiLevel[] {
    return (
      this._allLevels.value
        ?.filter(
          cll =>
            cll.subject === this._subject.value &&
            !(
              this._mathCategory.value !== LevumiMathCategory.MUL &&
              (cll.level === 'Station 6' || cll.level === 'Station 5')
            ),
        )
        .sort((a, b) => (a.level.trim().toUpperCase() < b.level.trim().toUpperCase() ? -1 : 1)) ?? []
    );
  }
}
